// This module will contain the core fluid simulation logic.
// It will need access to GL programs, FBOs, config, and blit function.

export function step(
  gl,
  config,
  ext,
  velocity,
  dye,
  curl,
  pressure,
  divergence,
  curlProgram,
  vorticityProgram,
  divergenceProgram,
  clearProgram,
  pressureProgram,
  gradienSubtractProgram,
  advectionProgram,
  webglBlitFunc,
  dt // Added dt as a direct parameter
) {
  gl.disable(gl.BLEND);

  // Curl
  curlProgram.bind();
  gl.uniform2f(
    curlProgram.uniforms.texelSize,
    velocity.texelSizeX,
    velocity.texelSizeY
  );
  gl.uniform1i(curlProgram.uniforms.uVelocity, velocity.read.attach(0));
  webglBlitFunc(gl, curl);

  // Vorticity
  vorticityProgram.bind();
  gl.uniform2f(
    vorticityProgram.uniforms.texelSize,
    velocity.texelSizeX,
    velocity.texelSizeY
  );
  gl.uniform1i(vorticityProgram.uniforms.uVelocity, velocity.read.attach(0));
  gl.uniform1i(vorticityProgram.uniforms.uCurl, curl.attach(1));
  gl.uniform1f(vorticityProgram.uniforms.curl, config.CURL);
  gl.uniform1f(vorticityProgram.uniforms.dt, dt); // Use direct dt parameter
  webglBlitFunc(gl, velocity.write);
  velocity.swap();

  // Divergence
  divergenceProgram.bind();
  gl.uniform2f(
    divergenceProgram.uniforms.texelSize,
    velocity.texelSizeX,
    velocity.texelSizeY
  );
  gl.uniform1i(divergenceProgram.uniforms.uVelocity, velocity.read.attach(0));
  webglBlitFunc(gl, divergence);

  // Clear pressure
  clearProgram.bind();
  gl.uniform1i(clearProgram.uniforms.uTexture, pressure.read.attach(0));
  gl.uniform1f(clearProgram.uniforms.value, config.PRESSURE);
  webglBlitFunc(gl, pressure.write);
  pressure.swap();

  // Pressure
  pressureProgram.bind();
  gl.uniform2f(
    pressureProgram.uniforms.texelSize,
    velocity.texelSizeX,
    velocity.texelSizeY
  );
  gl.uniform1i(pressureProgram.uniforms.uDivergence, divergence.attach(0));
  for (let i = 0; i < config.PRESSURE_ITERATIONS; i++) {
    gl.uniform1i(pressureProgram.uniforms.uPressure, pressure.read.attach(1));
    webglBlitFunc(gl, pressure.write);
    pressure.swap();
  }

  // Gradient Subtract
  gradienSubtractProgram.bind();
  gl.uniform2f(
    gradienSubtractProgram.uniforms.texelSize,
    velocity.texelSizeX,
    velocity.texelSizeY
  );
  gl.uniform1i(
    gradienSubtractProgram.uniforms.uPressure,
    pressure.read.attach(0)
  );
  gl.uniform1i(
    gradienSubtractProgram.uniforms.uVelocity,
    velocity.read.attach(1)
  );
  webglBlitFunc(gl, velocity.write);
  velocity.swap();

  // Advection
  advectionProgram.bind();
  gl.uniform2f(
    advectionProgram.uniforms.texelSize,
    velocity.texelSizeX,
    velocity.texelSizeY
  );
  if (!ext.supportLinearFiltering)
    gl.uniform2f(
      advectionProgram.uniforms.dyeTexelSize,
      velocity.texelSizeX,
      velocity.texelSizeY
    );
  let velocityId = velocity.read.attach(0);
  gl.uniform1i(advectionProgram.uniforms.uVelocity, velocityId);
  gl.uniform1i(advectionProgram.uniforms.uSource, velocityId);
  gl.uniform1f(advectionProgram.uniforms.dt, dt); // Use direct dt parameter
  gl.uniform1f(
    advectionProgram.uniforms.dissipation,
    config.VELOCITY_DISSIPATION
  );
  webglBlitFunc(gl, velocity.write);
  velocity.swap();

  if (!ext.supportLinearFiltering)
    gl.uniform2f(
      advectionProgram.uniforms.dyeTexelSize,
      dye.texelSizeX,
      dye.texelSizeY
    );
  gl.uniform1i(advectionProgram.uniforms.uVelocity, velocity.read.attach(0));
  gl.uniform1i(advectionProgram.uniforms.uSource, dye.read.attach(1));
  gl.uniform1f(advectionProgram.uniforms.dt, dt); // Use direct dt parameter
  gl.uniform1f(
    advectionProgram.uniforms.dissipation,
    config.DENSITY_DISSIPATION
  );
  webglBlitFunc(gl, dye.write);
  dye.swap();
}

export function render(
  gl,
  target,
  displayMaterial,
  dye,
  webglBlitFunc,
  config
) {
  // Added config for SHADING
  gl.enable(gl.BLEND);
  if (config.darkMode) {
    gl.blendFunc(gl.ONE, gl.ONE); // Additive blending for dark backgrounds
  } else {
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA); // Default alpha blending
  }

  drawDisplay(gl, target, displayMaterial, dye, webglBlitFunc, config);
}

function drawDisplay(gl, target, displayMaterial, dye, webglBlitFunc, config) {
  // Added config for SHADING
  let width = target == null ? gl.drawingBufferWidth : target.width;
  let height = target == null ? gl.drawingBufferHeight : target.height;
  displayMaterial.bind();
  if (config.SHADING) {
    // Access SHADING from config
    gl.uniform2f(displayMaterial.uniforms.texelSize, 1.0 / width, 1.0 / height);
  }
  gl.uniform1i(displayMaterial.uniforms.uTexture, dye.read.attach(0));
  webglBlitFunc(gl, target);
}

// Input Splatting Logic
export function splatPointer(
  gl,
  pointer,
  config,
  canvas,
  velocity,
  dye,
  splatProgram,
  webglBlitFunc
) {
  let dx = pointer.deltaX * config.SPLAT_FORCE;
  let dy = pointer.deltaY * config.SPLAT_FORCE;
  splat(
    gl,
    pointer.texcoordX,
    pointer.texcoordY,
    dx,
    dy,
    pointer.color,
    canvas,
    velocity,
    dye,
    splatProgram,
    config,
    webglBlitFunc
  );
}

// clickSplat is a specific behavior, might be kept in main.js or moved if generic enough
// It needs getNextColor and config to generate its own color.
export function clickSplat(
  gl,
  pointer,
  canvas,
  velocity,
  dye,
  splatProgram,
  config,
  webglBlitFunc,
  getNextColorFunc
) {
  const color = getNextColorFunc(config); // Call with config
  const clickColor = { r: color.r, g: color.g, b: color.b };
  // Random delta values for a more dynamic splat
  let dx = 10 * (Math.random() - 0.5);
  let dy = 30 * (Math.random() - 0.5);
  splat(
    gl,
    pointer.texcoordX,
    pointer.texcoordY,
    dx,
    dy,
    clickColor,
    canvas,
    velocity,
    dye,
    splatProgram,
    config,
    webglBlitFunc
  );
}

// Core splat function
function splat(
  gl,
  x,
  y,
  dx,
  dy,
  color,
  canvas,
  velocity,
  dye,
  splatProgram,
  config,
  webglBlitFunc
) {
  splatProgram.bind();
  gl.uniform1i(splatProgram.uniforms.uTarget, velocity.read.attach(0));
  gl.uniform1f(splatProgram.uniforms.aspectRatio, canvas.width / canvas.height);
  gl.uniform2f(splatProgram.uniforms.point, x, y);
  gl.uniform3f(splatProgram.uniforms.color, dx, dy, 0.0);
  gl.uniform1f(
    splatProgram.uniforms.radius,
    correctRadius(config.SPLAT_RADIUS / 100.0, canvas) // Pass canvas to correctRadius
  );
  webglBlitFunc(gl, velocity.write);
  velocity.swap();

  gl.uniform1i(splatProgram.uniforms.uTarget, dye.read.attach(0));
  gl.uniform3f(splatProgram.uniforms.color, color.r, color.g, color.b);
  webglBlitFunc(gl, dye.write);
  dye.swap();
}

function correctRadius(radius, canvas) {
  // Pass canvas
  let aspectRatio = canvas.width / canvas.height;
  if (aspectRatio > 1) radius *= aspectRatio;
  return radius;
}
